import React from "react";
import PageTitle from "@cs/components/src/PageTitle";
import Button from "@cs/components/src/Button";
import { Eye, RotateCcw, Search } from "react-feather";
import Table from "@cs/components/src/Table";
import Section from "@cs/components/src/Section";
import Spinner from "@cs/components/src/Spinner";
import { useMutation, useQuery } from "react-query";
import ApiService from "../../services/ApiService";
import { PageWrapper } from "@cs/components/src/PageWrapper";
import { styled } from "styled-components";
import ComponentTypeBadge from "./ComponentTypeBadge";
import { Link, useParams } from "react-router-dom";
import { useUpdateComponentsJob } from "../../context/UpdateComponentsJobContext";
import Badge from "@cs/components/src/Badge";
import Input from "@cs/components/src/Input";
import Fuse from "fuse.js";

interface PluginConfigItemExtra extends PluginConfigItem {
  organizations: string[];
}

interface RowProps {
  value: PluginConfigItemExtra | ScriptConfigItem;
}

const Flex = styled.div`
  display: flex;
  align-items: center;

  > * + * {
    margin-left: 10px;
  }
`;
const Organizations = styled.div`
  max-height: 110px;
  overflow-y: auto;
`;
const Buttons = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
`;
const ListRow = (props: RowProps) => {
  const { type } = useParams();
  const { value } = props;
  const { addComponentsJob } = useUpdateComponentsJob();

  const updateOrganizationsMutation = useMutation({
    mutationFn: () =>
      ApiService.fetch<{ scriptExecutionId: string; systemDomainId: string }>(
        "POST",
        `/api/config/${type}/${value.id}/update_organizations`
      ),
    onSuccess: (data) =>
      addComponentsJob(
        data.scriptExecutionId,
        data.systemDomainId,
        "Updating components"
      ),
  });

  return (
    <>
      <Flex>
        <span>{value.title}</span>
        {value.development && (
          <Badge color="var(--orange100)" label="Development" />
        )}
      </Flex>
      {"components" in value && (
        <>
          <span>
            {value.components
              .filter(({ type }) => type !== "list_input_config")
              .map(({ type }, index) => (
                <ComponentTypeBadge type={type} key={index} />
              ))}
          </span>
          <Organizations>{value.organizations.join("\n")}</Organizations>
        </>
      )}
      <Buttons>
        <Link to={`/config/${type}/${value.id}/${type}`}>
          <Button label="View Definition" icon={Eye} type="default" />
        </Link>
        {type === "plugin" && (
          <Button
            label="Update Organizations"
            icon={RotateCcw}
            type="default"
            onClick={() => updateOrganizationsMutation.mutate()}
            loading={updateOrganizationsMutation.isLoading}
            borderless
          />
        )}
      </Buttons>
    </>
  );
};

const ConfigurePluginScriptListScreen = () => {
  const { type } = useParams();
  const title = type === "plugin" ? "Plugin" : "Script";

  const { data, isLoading, isError } = useQuery({
    queryKey: ["configList", type],
    queryFn: async () => {
      const response = await ApiService.fetch<{
        data: (PluginConfigItemExtra | ScriptConfigItem)[];
      }>("GET", `/api/config/${type}`);
      return response.data;
    },
  });
  const [search, setSearch] = React.useState("");

  let rows = data ?? [];
  if (search) {
    const fuse = new Fuse<PluginConfigItemExtra | ScriptConfigItem>(
      rows ?? [],
      {
        keys: ["title", "organizations"],
        ignoreLocation: true,
        threshold: 0.2,
        shouldSort: false,
      }
    );

    rows = fuse.search(search).map((result) => result.item);
  }

  const columns =
    type === "plugin"
      ? [
          { title: "Name", width: "1fr" },
          { title: "Components", width: "1fr" },
          { title: "Instances", width: "1fr" },
          { width: "200px" },
        ]
      : [{ title: "Name", width: "1fr" }, { width: "200px" }];

  return (
    <div>
      <PageTitle
        title={`Configured ${title}s`}
        error={isError ? `Problem loading ${type}s` : undefined}
      >
        <Input
          value={search}
          onChange={setSearch}
          placeholder="Search"
          icon={Search}
          clearable
        />
        {/* <Button label={`New ${title}`} icon={Plus} link={`/config/${type}/new/${type}`} type="success" /> */}
      </PageTitle>

      {isLoading ? (
        <Spinner />
      ) : (
        !isError && (
          <PageWrapper>
            <Section>
              <Table
                columnGrid
                columns={columns}
                rows={
                  rows.map((value) => ({
                    id: value.id,
                    element: () => <ListRow value={value} />,
                  })) ?? []
                }
                autoHeight={{}}
              />
            </Section>
          </PageWrapper>
        )
      )}
    </div>
  );
};

export default ConfigurePluginScriptListScreen;
